import { Injectable } from '@angular/core';
import MediaSwitcher from 'media-switcher';
import { MediaManagerService } from './media-manager.service';

declare const window: any;

@Injectable({
    providedIn: 'root'
})
export class RecorderService extends MediaManagerService {
    private mediaSwitcher: MediaSwitcher;
    private recorderStream: MediaStream | null = null;
    private mediaRecorder: MediaRecorder | null = null;
    private chunks: Blob[] = [];
    private readonly mimeType: string = 'video/webm; codecs=vp8,opus';
    private starting: boolean = false;
    private recording: boolean = false;
    private currentBlob: Blob | null = null;

    constructor() {
        super();
        this.mediaSwitcher = new MediaSwitcher();
    }

    isRecording(): boolean {
        return this.recording;
    }

    async start(initialStream?: MediaStream): Promise<boolean> {
        if (this.starting || this.recording) {
            return Promise.reject('Registrazione già in corso');
        }

        this.starting = true;
        try {
            // Usa lo stream iniziale o il combinato attuale
            const baseStream = initialStream || this.getCombinedStream();
            this.recorderStream = await this.mediaSwitcher.initialize(baseStream);
            if (!this.recorderStream) {
                return Promise.reject('Nessuno stream disponibile per la registrazione');
            }
            this.chunks = [];

            this.mediaRecorder = new MediaRecorder(this.recorderStream, { mimeType: this.mimeType });

            this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
                if (event.data && event.data.size > 0) {
                    this.chunks.push(event.data);
                }
            };

            this.mediaRecorder.start();
            this.recording = true;
            console.log('Registrazione avviata');
            return true;
        } catch (error) {
            console.error('Errore nell’avvio della registrazione:', error);
            this.recording = false;
            return Promise.reject('Errore nell’avvio della registrazione');
        } finally {
            this.starting = false;
        }
    }

    stop(): Promise<Blob> {
        return new Promise((resolve, reject) => {
            if (!this.recording || !this.mediaRecorder) {
                return reject('La registrazione non è attiva');
            }

            this.mediaRecorder.onstop = () => {
                const blob = new Blob(this.chunks, { type: this.mimeType });
                this.currentBlob = blob;
                this.recording = false;
                console.log('Registrazione terminata');
                resolve(blob);
            };

            this.mediaRecorder.stop();
        });
    }

    download(filename: string = 'recording.webm'): void {
        const blob = this.currentBlob || new Blob(this.chunks, { type: this.mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();

        setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }, 100);
    }

    setOnWindow(key: string = 'recordedBlob'): void {
        const blob = this.currentBlob || new Blob(this.chunks, { type: this.mimeType });
        window[key] = blob;
    }

    /**
     * Implementazione dei metodi richiesti da MediaManagerService
     */
    addLocalTrack(track: MediaStreamTrack): void {
        console.log('addLocalTrack', track);
        this.localStream.addTrack(track);
        this.mediaSwitcher.changeTrack(track); // aggiorna dinamicamente
    }

    removeLocalTrack(track: MediaStreamTrack): void {
        this.localStream.removeTrack(track);
        // media-switcher non ha "remove", quindi potresti voler chiamare changeStream con lo stream aggiornato
        // this.mediaSwitcher.changeStream(this.getCombinedStream());
        this.mediaSwitcherChangeStream(this.getCombinedStream());
    }

    addRemoteTrack(track: MediaStream | MediaStreamTrack): void {
        console.log('addRemoteTrack', track);
        if (track instanceof MediaStream) {
            track.getTracks().forEach(t => this.remoteStream.addTrack(t));
        } else {
            this.remoteStream.addTrack(track);
        }
        // this.mediaSwitcher.changeStream(this.getCombinedStream());
        this.mediaSwitcherChangeStream(this.getCombinedStream());
    }

    removeRemoteTrack(track: MediaStreamTrack): void {
        this.remoteStream.removeTrack(track);
        // this.mediaSwitcher.changeStream(this.getCombinedStream());
        this.mediaSwitcherChangeStream(this.getCombinedStream());
    }

    mediaSwitcherChangeStream(stream: MediaStream) {
        // this.mediaSwitcher.changeStream(stream);
        console.log('RECOREDERSERVICE mediaSwitcherChangeStream', stream.constructor.name, stream, stream.getTracks());
        if (!stream || stream.constructor.name !== 'MediaStream') return;
        stream.getTracks().forEach(track => {
            this.mediaSwitcher.changeTrack(track);
        });
    }

    setRemoteMediaStream(mediaStream: MediaStream): void {
        if(!mediaStream) return;
        console.log('RECOREDERSERVICE setRemoteMediaStream', mediaStream.constructor.name, mediaStream, mediaStream.getTracks());
        this.remoteStream = mediaStream;
        const combined = this.getCombinedStream();
        console.log('RECOREDERSERVICE setRemoteMediaStream combined', mediaStream.constructor.name, mediaStream, mediaStream.getTracks());
        // combined.getTracks().forEach(track => this.mediaSwitcher.changeTrack(track));
        this.mediaSwitcherChangeStream(combined);
    }

    setLocalMediaStream(mediaStream: MediaStream): void {
        this.localStream = mediaStream;
        // this.mediaSwitcher.changeStream(this.getCombinedStream());
        const combined = this.getCombinedStream();
        combined.getTracks().forEach(track => this.mediaSwitcher.changeTrack(track));
    }

    /**
     * Restituisce uno stream unificato local + remote
     */
    private getCombinedStream(): MediaStream {
        const combined = new MediaStream();
        this.localStream?.getTracks().forEach(track => combined.addTrack(track));
        this.remoteStream?.getTracks().forEach(track => combined.addTrack(track));
        return combined;
    }
}
